import ImagePreviewCarousel, {
  IImagePreviewItem,
  ITrailingContent,
} from "@/components/ImagePreviewCarousel";
import { H3, H5, P } from "@/components/Typography";
import { useTranslation } from "react-i18next";
import { doesUserHaveScope } from "@/utils/doesUserHaveScope";
import { useUser } from "@/requests/hooks/user";
import { Alert } from "@/components/Alert";
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { Button } from "@/components/Button";
import { Toggle } from "@/components/Toggle";
import { useState, useEffect } from "react";
import { Modal } from "@/components/Modal";
import { useFlags } from "flagsmith/react";
import { toast } from "react-toastify";
import {
  usePostTogglePoweredByDatashipsPreference,
  useGetPoweredByDatashipsPreference,
} from "@/requests/hooks/collect.ts";
import { IFeatureFlagKeys } from "@/utils/featureFlags.ts";

import emailSoftOptIn from "@/assets/images/collect/email-widget-soft-opt-in.png";
import emailSoftOptInToggled from "@/assets/images/collect/email-widget-soft-opt-in-toggled.png";

import emailSoftOptOut from "@/assets/images/collect/email-widget-soft-opt-out.png";
import emailSoftOptOutToggled from "@/assets/images/collect/email-widget-soft-opt-out-toggled.png";

import emailImpliedConsent from "@/assets/images/collect/email-widget-implied-consent.png";
import emailImpliedConsentToggled from "@/assets/images/collect/email-widget-implied-consent-toggled.png";

import smsSoftOptIn from "@/assets/images/collect/sms-widget-soft-opt-in.png";
import smsSoftOptInToggled from "@/assets/images/collect/sms-widget-soft-opt-in-toggled.png";

import smsExplicitOptIn from "@/assets/images/collect/sms-widget-explicit-opt-in.png";
import smsExplicitOptInToggled from "@/assets/images/collect/sms-widget-explicit-opt-in-toggled.png";

import widgetModalContent from "@/assets/images/collect/widget-modal-content.png";

const Preview = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [customTrailingContent, setCustomTrailingContent] =
    useState<ITrailingContent>();

  const flags = useFlags<IFeatureFlagKeys>(["display-widget-footer-enabled"]);
  const poweredByDatashipsEnabled =
    flags?.["display-widget-footer-enabled"].enabled ?? true;

  const { data: user } = useUser();

  const {
    data: isGetPoweredByDatashipsData,
    refetch: refetchPoweredByDataships,
  } = useGetPoweredByDatashipsPreference();

  const {
    mutate: togglePoweredByDataships,
    isSuccess: isTogglePoweredByDatashipsSuccess,
  } = usePostTogglePoweredByDatashipsPreference();

  const onTogglePoweredByDataships = () => {
    togglePoweredByDataships(!isGetPoweredByDatashipsData?.enabled);
  };

  useEffect(() => {
    if (isTogglePoweredByDatashipsSuccess) {
      toast.success(t("settings.widgetSettings.preview.poweredBy.success"));
      refetchPoweredByDataships();
    }
  }, [isTogglePoweredByDatashipsSuccess]);

  const smsImages: IImagePreviewItem[] = [
    {
      title: t("settings.widgetSettings.preview.titles.smsSoftOptIn"),
      image: smsSoftOptIn,
    },
    {
      title: t("settings.widgetSettings.preview.titles.smsExplicitOptIn"),
      image: smsExplicitOptIn,
      trailingContent: {
        title: t(
          "settings.widgetSettings.preview.trailingContent.smsOptInTitle",
        ),
        description: t(
          "settings.widgetSettings.preview.trailingContent.smsOptInDescription",
        ),
      },
    },
  ];

  const smsImagesToggled: IImagePreviewItem[] = [
    {
      title: t("settings.widgetSettings.preview.titles.smsSoftOptIn"),
      image: smsSoftOptInToggled,
    },
    {
      title: t("settings.widgetSettings.preview.titles.smsExplicitOptIn"),
      image: smsExplicitOptInToggled,
      trailingContent: {
        title: t(
          "settings.widgetSettings.preview.trailingContent.smsOptInTitle",
        ),
        description: t(
          "settings.widgetSettings.preview.trailingContent.smsOptInDescription",
        ),
      },
    },
  ];

  const emailImages: IImagePreviewItem[] = [
    {
      title: t("settings.widgetSettings.preview.titles.emailSoftOptIn"),
      image: emailSoftOptIn,
    },
    {
      title: t("settings.widgetSettings.preview.titles.emailSoftOptOut"),
      image: emailSoftOptOut,
    },
    {
      title: t("settings.widgetSettings.preview.titles.emailImpliedConsent"),
      image: emailImpliedConsent,
    },
  ];

  const emailImagesToggled: IImagePreviewItem[] = [
    {
      title: t("settings.widgetSettings.preview.titles.emailSoftOptIn"),
      image: emailSoftOptInToggled,
    },
    {
      title: t("settings.widgetSettings.preview.titles.emailSoftOptOut"),
      image: emailSoftOptOutToggled,
    },
    {
      title: t("settings.widgetSettings.preview.titles.emailImpliedConsent"),
      image: emailImpliedConsentToggled,
    },
  ];

  const getImages = () => {
    if (isGetPoweredByDatashipsData?.enabled) {
      return emailImages.concat(
        doesUserHaveScope("LIST_SMS_CONTACTS", user?.scopes) ? smsImages : [],
      );
    } else {
      return emailImagesToggled.concat(
        doesUserHaveScope("LIST_SMS_CONTACTS", user?.scopes)
          ? smsImagesToggled
          : [],
      );
    }
  };

  const toggleModal = () => setIsModalOpen(isModalOpen => !isModalOpen);

  const setTrailingContent = (trailingContent?: ITrailingContent) => {
    if (trailingContent) {
      setCustomTrailingContent(trailingContent);
    } else if (customTrailingContent) {
      setCustomTrailingContent(undefined);
    }
  };

  const renderHeader = () => (
    <>
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.widgetSettings.preview.title")}
        </H3>
      </div>
      <P>{t("settings.widgetSettings.preview.description")}</P>
    </>
  );

  const renderTrailingContentTitle = () => (
    <div className="flex flex-grow flex-col items-center justify-between gap-10 md:flex-row md:gap-30">
      <div>
        <div className="mb-10">
          <H5 className="font-bold" color="secondary">
            {!!customTrailingContent?.title
              ? customTrailingContent?.title
              : t("settings.widgetSettings.preview.trailingContent.title")}
          </H5>
        </div>
        <P>
          {!!customTrailingContent?.description
            ? customTrailingContent?.description
            : t("settings.widgetSettings.preview.trailingContent.description")}
        </P>
      </div>
      {!customTrailingContent && (
        <Button
          variant="secondary-outline"
          width="md"
          onClick={toggleModal}
          trackingId="control-panel.widget-settings.preview.view-modal"
          testId="toggle-widget-modal">
          {t("settings.widgetSettings.preview.trailingContent.cta")}
        </Button>
      )}
    </div>
  );

  const renderTrailingContent = () => (
    <Alert
      variant="info"
      icon={faInfoCircle}
      titleSlot={renderTrailingContentTitle()}
    />
  );

  const renderImagePreviewCarousel = () => (
    <ImagePreviewCarousel
      images={getImages()}
      identifier="Widget Preview"
      imageTrailingContent={renderTrailingContent()}
      setTrailingContent={setTrailingContent}
    />
  );

  const renderToggleForPoweredByDataships = () => (
    <div className="mb-10 mt-20 flex justify-between">
      <div>
        <H5 className="font-bold" color="secondary">
          {t("settings.widgetSettings.preview.poweredBy.title")}
        </H5>
        <P>{t("settings.widgetSettings.preview.poweredBy.description")}</P>
      </div>
      <div className="flex items-center">
        <Toggle
          enabled={isGetPoweredByDatashipsData?.enabled ?? true}
          onToggle={onTogglePoweredByDataships}
        />
      </div>
    </div>
  );

  const renderModal = () => (
    <Modal
      rounded="sm"
      isOpen={isModalOpen}
      toggleIsOpen={toggleModal}
      align="center">
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.widgetSettings.preview.modal.title")}
        </H3>
      </div>
      <P>{t("settings.widgetSettings.preview.modal.description")}</P>
      <div className="mt-30 flex justify-center md:min-h-[540px]">
        <img className="max-w-full" src={widgetModalContent} />
      </div>
    </Modal>
  );

  return (
    <>
      {renderHeader()}
      {renderImagePreviewCarousel()}
      {poweredByDatashipsEnabled && renderToggleForPoweredByDataships()}
      {renderModal()}
    </>
  );
};

export default Preview;
